import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {IdDto} from '@services/associations/model/idDto';
import {StreetDto} from '@services/associations/model/streetDto';
import {DictionariesService} from '@shared/services/dictionaries.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from 'rxjs';
import {map} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-add-street-dialog',
    templateUrl: './add-street-dialog.component.html',
    styleUrls: ['./add-street-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddStreetDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    streetForm: UntypedFormGroup;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            cityId: string,
            street?: StreetDto
        },
        public dialogRef: MatDialogRef<AddStreetDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private dictionariesService: DictionariesService,
        private translateService: TranslateService,
        private toastrService: ToastrService
    ) {
    }

    ngOnInit(): void {
        this.initForm();
    }

    ngOnDestroy() {
        this.destroyed$.complete();
        this.loading$.complete();
    }

    initForm() {
        this.streetForm = this.formBuilder.group({
            type: [this.data.street?.type ? this.data.street.type : 'STREET', [Validators.required]],
            name: [this.data.street?.name ? this.data.street.name : '', [Validators.required]]
        });
    }

    submitForm() {
        const formData = this.streetForm.getRawValue();
        const query = {
            name: formData.name,
            type: formData.type
        };

        this.loading$.next(true);
        if (this.data.street?.id) {
            this.editStreet(query);
        } else {
            this.addStreet(query);
        }
    }

    addStreet(query: any) {
        const request = {cityId: this.data?.cityId, ...query};
        this.dictionariesService.addStreet(request).pipe(
            takeUntil(this.destroyed$),
            map((response: IdDto) => {
                if (response.id) {
                    this.closeDialog(true, {id: response.id, ...query});
                }
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    editStreet(query: any) {
        const request = {id: this.data?.street?.id, cityId: this.data?.cityId, ...query};
        this.dictionariesService.updateStreet(request).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.closeDialog(true, request);
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
